import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { BayenahLogo } from '@/components/brand/BayenahLogo';
import { blogPosts } from './BlogPost';

export default function BlogIndex() {
  useEffect(() => {
    document.title = 'المدونة | بينة — مقالات قانونية سعودية';
    document.documentElement.lang = 'ar';
    document.documentElement.dir = 'rtl';

    // Update meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const description = 'مقالات ودلائل قانونية سعودية شاملة. مكافأة نهاية الخدمة، مراجعة العقود، الأنظمة السعودية، وغيرها.';
    updateMeta('description', description);
    updateMeta('og:title', 'المدونة | بينة', true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', 'https://bayenah.com/blog', true);
    updateMeta('og:type', 'website', true);

    window.scrollTo(0, 0);
  }, []);

  // Convert blogPosts object to array and sort by date (newest first)
  const posts = Object.entries(blogPosts)
    .map(([slug, post]) => ({ slug, ...post }))
    .sort((a, b) => new Date(b.dateISO) - new Date(a.dateISO));

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-white" dir="rtl">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#F7F7F5]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-[#111111] transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            <span>العودة إلى بينة</span>
          </Link>
          <Link to="/">
            <BayenahLogo height={28} variant="inverted" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-arabic">
            المدونة
          </h1>
          <p className="text-lg text-white/60 font-arabic">
            مقالات ودلائل قانونية سعودية شاملة
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="space-y-6">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="block p-6 md:p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#111111]/30 hover:bg-white/[0.04] transition-all group"
            >
              <div className="flex items-center gap-2 text-sm text-white/40 mb-4">
                <Calendar className="w-4 h-4" />
                <span className="font-arabic">{post.date}</span>
              </div>
              
              <h2 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-[#111111] transition-colors font-arabic">
                {post.title}
              </h2>
              
              <p className="text-white/60 leading-relaxed mb-4 font-arabic">
                {post.excerpt}
              </p>
              
              <span className="inline-flex items-center gap-2 text-[#111111] text-sm font-medium">
                <span className="font-arabic">اقرأ المزيد</span>
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-[#111111]/10 to-[#111111]/5 border border-[#111111]/20 text-center">
          <h3 className="text-xl font-bold text-white mb-3 font-arabic">
            هل عندك سؤال قانوني؟
          </h3>
          <p className="text-white/70 mb-6 font-arabic">
            اسأل بينة واحصل على إجابة فورية مع ذكر المادة النظامية
          </p>
          <Link 
            to="/" 
            className="inline-block px-8 py-3 bg-[#111111] text-black font-bold rounded-lg hover:bg-[#111111] transition-all shadow-[0_0_20px_rgba(0, 0, 0,0.3)] hover:shadow-[0_0_30px_rgba(0, 0, 0,0.4)]"
          >
            جرّب بينة الآن
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/">
            <BayenahLogo height={24} variant="inverted" />
          </Link>
          <p className="text-xs text-white/40 font-arabic">© 2026 بينة. جميع الحقوق محفوظة.</p>
        </div>
      </footer>
    </div>
  );
}
